/**
 * OfflineIndicator - Componente que mostra banner persistente quando offline
 *
 * Mostra um banner na parte superior da tela quando o app está em modo offline.
 * O banner desaparece automaticamente quando a conexão é restaurada.
 *
 * Features:
 * - Detecta mudanças de conectividade em tempo real
 * - Banner com animação suave
 * - Botão para forçar sincronização quando online
 * - Compatível com o design system existente (cores Zoetis)
 *
 * Ref: docs/INVESTIGACAO_COMPLETA_BUG_OFFLINE.md
 *
 * @since 2025-12-12
 */

import React, { Component } from 'react'
import { Tooltip, Snackbar, IconButton } from '@mui/material'
import WifiOffIcon from '@mui/icons-material/WifiOff'
import SyncIcon from '@mui/icons-material/Sync'
import CloseIcon from '@mui/icons-material/Close'
import { connect } from 'react-redux'
import { recordEvent } from '../observability/events'

class OfflineIndicator extends Component {
  state = {
    isOffline: !navigator.onLine,
    showBanner: !navigator.onLine,
    dismissed: false,
    lastOnlineTime: navigator.onLine ? Date.now() : null,
  }

  componentDidMount() {
    window.addEventListener('online', this.handleOnline)
    window.addEventListener('offline', this.handleOffline)
  }

  componentWillUnmount() {
    window.removeEventListener('online', this.handleOnline)
    window.removeEventListener('offline', this.handleOffline)
  }

  handleOnline = () => {
    const offlineSinceMs = this.state.lastOnlineTime
      ? Date.now() - this.state.lastOnlineTime
      : null
    recordEvent({
      level: 'info',
      category: 'sync',
      action: 'network_online',
      payload: { offlineDurationMs: offlineSinceMs },
    })

    this.setState({
      isOffline: false,
      showBanner: false,
      dismissed: false,
      lastOnlineTime: Date.now(),
    })

    if (this.props.onBackOnline) {
      this.props.onBackOnline()
    }
  }

  handleOffline = () => {
    recordEvent({
      level: 'warn',
      category: 'sync',
      action: 'network_offline',
    })

    this.setState({
      isOffline: true,
      showBanner: true,
      dismissed: false,
    })
  }

  handleDismiss = () => {
    this.setState({ dismissed: true, showBanner: false })
  }

  handleForceSync = () => {
    if (this.props.onForceSync) {
      this.props.onForceSync()
    }
  }

  getOfflineMessage = () => {
    const { lastOnlineTime } = this.state

    if (lastOnlineTime) {
      const minutesAgo = Math.floor((Date.now() - lastOnlineTime) / 60000)
      if (minutesAgo < 1) {
        return 'Você está offline. Suas alterações serão sincronizadas quando a conexão for restaurada.'
      } else if (minutesAgo < 60) {
        return `Você está offline há ${minutesAgo} minuto(s). Dados locais estão disponíveis.`
      } else {
        const hoursAgo = Math.floor(minutesAgo / 60)
        return `Você está offline há ${hoursAgo} hora(s). Dados locais estão disponíveis.`
      }
    }

    return 'Você está offline. O app está funcionando com dados locais.'
  }

  render() {
    const { isOffline, showBanner, dismissed } = this.state

    // Se online ou banner foi fechado, não mostrar nada
    if (!isOffline) {
      return null
    }

    // Banner principal quando offline
    if (showBanner && !dismissed) {
      return (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            backgroundColor: '#ff9800',
            color: '#fff',
            padding: '12px 16px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            zIndex: 9999,
            boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
            animation: 'slideDown 0.3s ease-out',
          }}
        >
          <style>{`
            @keyframes slideDown {
              from {
                transform: translateY(-100%);
                opacity: 0;
              }
              to {
                transform: translateY(0);
                opacity: 1;
              }
            }
          `}</style>

          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <WifiOffIcon style={{ fontSize: '24px' }} />
            <span style={{ fontSize: '14px', fontWeight: 500 }}>
              {this.getOfflineMessage()}
            </span>
          </div>

          <IconButton
            size='small'
            onClick={this.handleDismiss}
            style={{ color: '#fff' }}
          >
            <CloseIcon fontSize='small' />
          </IconButton>
        </div>
      )
    }

    // Indicador compacto quando banner foi fechado mas ainda está offline
    if (dismissed && isOffline) {
      return (
        <Tooltip title='Modo offline - clique para ver detalhes' arrow>
          <div
            style={{
              position: 'fixed',
              bottom: '80px',
              right: '20px',
              backgroundColor: '#ff9800',
              color: '#fff',
              borderRadius: '50%',
              width: '48px',
              height: '48px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 4px 8px rgba(0,0,0,0.3)',
              cursor: 'pointer',
              zIndex: 9998,
            }}
            onClick={() =>
              this.setState({ showBanner: true, dismissed: false })
            }
          >
            <WifiOffIcon style={{ fontSize: '24px' }} />
          </div>
        </Tooltip>
      )
    }

    return null
  }
}

// Conectar ao Redux para ter acesso ao estado de sync se necessário
const mapStateToProps = (state) => ({
  currentSync: state.sync?.currentSync,
})

export default connect(mapStateToProps)(OfflineIndicator)
